import { FrequencyInfo } from '../client-route-locator/client-frequency-display/client-frequency-display.interface';
import { DAYS_MAP } from '../constants/days-list.constant';

/**
 * Computed frequency data added to business partner records
 */
export interface ComputedFrequencyData {
  bpDayFrecuenciesLength: number;
  bpCustomFrecuenciesLength: number;
  bpDaysInAllFrecuencies: string;
}

/**
 * Computes a comma-separated string of day abbreviations from an array of frequencies.
 * Extracts all unique days from frequencies and sorts them by day code (1-7).
 *
 * @param frequencies - Array of frequency objects containing days arrays
 * @returns Comma-separated day abbreviations (e.g., "LU, MA, MI") or empty string if no frequencies
 *
 * @example
 * computeDaysText([{ days: [1, 3] }, { days: [2, 3] }]) // "LU, MA, MI"
 * computeDaysText([]) // ""
 * computeDaysText(null) // ""
 */
export function computeDaysText(frequencies: FrequencyInfo[] | null | undefined): string {
  if (!frequencies || frequencies.length === 0) return '';

  const allDays = frequencies.flatMap((f) => f.days || []);
  const uniqueDays = [...new Set(allDays)].sort((a, b) => a - b);

  return uniqueDays
    .map((code) => DAYS_MAP.get(code) || '')
    .filter(Boolean)
    .join(', ');
}

/**
 * Computes frequency statistics from an array of frequencies.
 *
 * @param frequencies - Array of frequency objects
 * @returns Object with day frequency count, custom frequency count, and days text
 *
 * @example
 * computeFrequencyData([
 *   { type: 'W', days: [1, 2] },
 *   { type: 'C', days: [] }
 * ])
 * // { bpDayFrecuenciesLength: 1, bpCustomFrecuenciesLength: 1, bpDaysInAllFrecuencies: "LU, MA" }
 */
export function computeFrequencyData(
  frequencies: FrequencyInfo[] | null | undefined
): ComputedFrequencyData {
  const safeFrequencies = frequencies || [];

  return {
    bpDayFrecuenciesLength: safeFrequencies.filter((f) => f.type === 'W').length,
    bpCustomFrecuenciesLength: safeFrequencies.filter((f) => f.type === 'C').length,
    bpDaysInAllFrecuencies: computeDaysText(safeFrequencies),
  };
}
